import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ResourceStatus } from 'libs.library_manager';
import { Table, useLocalizeMessage, Toggle } from 'libs.nucleus';
import { PageHeader, useBooleanState } from 'libs.react';
import { useDataViewsColumns } from './data_views_columns.hook';
import { useDataViews } from '../../hooks';
import { CreateViewModal } from '../create_view_modal/create_view_modal.component';
export const DataViewsDashboard = () => {
    const navigate = useNavigate();
    const translate = useLocalizeMessage();
    const [isCreateViewModalOpen, openCreateViewModal, closeCreateViewModal] = useBooleanState();
    const [showArchived, setShowArchived] = useState(false);
    const [statusFilter, setStatusFilter] = useState();
    const [dataViews, setDataViews] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { fetchDataViews, archiveDataView, isLoading: isApiLoading } = useDataViews();
    const handleArchiveView = useCallback(async (view) => {
        try {
            await archiveDataView(view);
            setIsLoading(true);
            try {
                const views = await fetchDataViews(showArchived, statusFilter);
                setDataViews(views);
            }
            catch (error) {
                // Error already handled in hook
                setDataViews([]);
            }
            finally {
                setIsLoading(false);
            }
        }
        catch (error) {
            // Error already handled in hook
        }
    }, [archiveDataView, fetchDataViews, showArchived, statusFilter]);
    const dataViewsColumns = useDataViewsColumns({
        archiveView: handleArchiveView,
    });
    useEffect(() => {
        let isMounted = true;
        const loadDataViews = async () => {
            setIsLoading(true);
            try {
                const views = await fetchDataViews(showArchived, statusFilter);
                if (isMounted) {
                    setDataViews(views);
                }
            }
            catch (error) {
                // Error already handled in hook
                if (isMounted) {
                    setDataViews([]);
                }
            }
            finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };
        loadDataViews();
        return () => {
            isMounted = false;
        };
    }, [fetchDataViews, showArchived, statusFilter]);
    const statusFilterFields = useMemo(() => [
        {
            fieldLabel: translate('Status'),
            fieldName: 'status',
            fieldKey: 'status',
            fieldWidth: 'sm',
            filterOptions: [
                { label: translate('In progress'), value: ResourceStatus.DRAFT },
                { label: translate('In review'), value: ResourceStatus.INREVIEW },
                { label: translate('Ready for use'), value: ResourceStatus.PUBLISHED },
            ],
        },
    ], [translate]);
    const handleStatusFilterChange = useCallback((filters) => {
        const selectedStatus = filters.status;
        if (selectedStatus && 'value' in selectedStatus) {
            setStatusFilter(selectedStatus.value);
            return;
        }
        setStatusFilter(undefined);
    }, []);
    const handleCreateViewSuccess = useCallback((view) => {
        closeCreateViewModal();
        navigate(`/library/data-views/${view.id}`);
    }, [closeCreateViewModal, navigate]);
    const hasNoResults = !isLoading && !isApiLoading && dataViews.length === 0;
    const headerOptions = [
        {
            name: translate('Create dataset'),
            onClick: openCreateViewModal,
            variant: 'primary',
            disabled: isLoading || isApiLoading,
        },
    ];
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: translate('Datasets'), subtitle: translate('Create and manage custom workspace views with dimensions and measures.'), LinkComponent: Link, options: headerOptions }), _jsx(Table, { isLoading: isLoading || isApiLoading, data: dataViews, columns: dataViewsColumns, labelNoResults: hasNoResults ? translate('No datasets found') : translate('No datasets found'), useInternalSearch: true, enableSorting: true, hasPagination: true, initialPageSize: 10, paginationNavigationScreenReaderLabel: translate('Datasets table navigation'), onFilter: handleStatusFilterChange, onFilterClear: () => setStatusFilter(undefined), filterFields: statusFilterFields, labelSearchField: translate('Search'), customHeaderContent: _jsx("div", { className: 'flex gap-4 ml-auto', children: _jsx("div", { className: 'border-border-primary border-1 rounded-lg py-2 px-4 whitespace-nowrap min-w-max', children: _jsx(Toggle, { label: translate('Show archived'), checked: showArchived, onChange: setShowArchived }) }) }) }), isCreateViewModalOpen && _jsx(CreateViewModal, { onClose: closeCreateViewModal, onSuccess: handleCreateViewSuccess })] }));
};
